import { useEffect, useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useUserRole } from "@/lib/useUserRole";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { TourPackageItem, TourItem, InquiryDayItem, CapturedToursMap, DayTourData } from "@/types/database";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";

interface InquiryDayFormProps {
  day: InquiryDayItem;
  adult: number;
  child: number;
  onUpdate: (updatedDay: InquiryDayItem) => void;
}

export function InquiryDayForm({
  day,
  adult,
  child,
  onUpdate,
}: {
  day: InquiryDayItem;
  adult: number;
  child: number;
  onUpdate: (day: InquiryDayItem) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedPackage, setSelectedPackage] = useState<string>(day.tour_package_id || "");
  const [transportArea, setTransportArea] = useState<"Kuta" | "Nusa Dua" | "Ubud" | "Other">(
    (day.transport_area as "Kuta" | "Nusa Dua" | "Ubud" | "Other") || "Kuta"
  );
  const [note, setNote] = useState<string>(day.note || "");
  
  // Get user role to determine if price editing is allowed
  const { role } = useUserRole();
  const canEditPrices = role === 'admin' || role === 'agent';

  const { data: tourPackages = [] } = useQuery({
    queryKey: ["tourPackages"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tour_package")
        .select(`
          id,
          tour_package_name,
          selected_tour,
          transport_kuta,
          transport_nusadua,
          transport_ubud,
          transport_other,
          elf_short_transport_kuta,
          elf_short_transport_nusadua,
          elf_short_transport_ubud,
          elf_short_transport_other,
          hiace_transport_kuta,
          hiace_transport_nusadua,
          hiace_transport_ubud,
          hiace_transport_other,
          elf_giga_transport_kuta,
          elf_giga_transport_nusadua,
          elf_giga_transport_ubud,
          elf_giga_transport_other,
          bus_transport_kuta,
          bus_transport_nusadua,
          bus_transport_ubud,
          bus_transport_other,
          itinerary
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as TourPackageItem[];
    },
  });

  const filteredPackages = tourPackages.filter((pkg) =>
    pkg.tour_package_name.toLowerCase().includes(search.toLowerCase())
  );

  // Calculate fees for a tour package
  const calculateFees = (packageId: string): DayTourData => {
    const pkg = tourPackages.find((p) => p.id === packageId);
    if (!pkg || !pkg.selected_tour) return { 
      title: '', 
      tours: [], 
      entrance_adult: {}, 
      entrance_child: {}, 
      parking: {}, 
      tour_package_name: '', 
      itinerary: '' 
    };

    // Parse the selected_tour JSON if it's a string
    let tours: TourItem[] = [];
    try {
      if (typeof pkg.selected_tour === 'string') {
        tours = JSON.parse(pkg.selected_tour);
      } else if (Array.isArray(pkg.selected_tour)) {
        tours = pkg.selected_tour;
      }
    } catch (e) {
      console.error('Error parsing selected_tour:', e);
      return { 
        title: '', 
        tours: [], 
        entrance_adult: {}, 
        entrance_child: {}, 
        parking: {}, 
        tour_package_name: '', 
        itinerary: '' 
      };
    }

    // Get the appropriate parking price field based on car type
    const getParkingPriceField = (carType: string) => {
      switch (carType) {
        case 'ELF Short':
          return 'elf_short_parking_price';
        case 'Hiace':
          return 'hiace_parking_price';
        case 'ELF Giga':
          return 'elf_giga_parking_price';
        case 'Bus':
          return 'bus_parking_price';
        case 'MPV':
        default:
          return 'mpv_parking_price';
      }
    };

    const parkingPriceField = getParkingPriceField(day.car_type || 'MPV');

    // Format fees into the new JSON structure
    return {
      title: '',
      tours: tours.map(tour => tour.tour_name),
      entrance_adult: tours.reduce((acc: { [key: string]: number }, tour: TourItem) => ({ 
        ...acc, 
        [tour.tour_name]: tour.tour_adult_price 
      }), {}),
      entrance_child: tours.reduce((acc: { [key: string]: number }, tour: TourItem) => ({ 
        ...acc, 
        [tour.tour_name]: tour.tour_child_price 
      }), {}),
      parking: tours.reduce((acc: { [key: string]: number }, tour: TourItem) => ({ 
        ...acc, 
        [tour.tour_name]: tour[parkingPriceField] 
      }), {}),
      tour_package_name: pkg.tour_package_name,
      itinerary: pkg.itinerary || ''
    };
  };

  // Get the current fees
  const currentFees = useMemo(() => {
    if (!day.tour_package_id) return { 
      title: '', 
      tours: [], 
      entrance_adult: {}, 
      entrance_child: {}, 
      parking: {}, 
      tour_package_name: '', 
      itinerary: '' 
    };
    return calculateFees(day.tour_package_id);
  }, [day.tour_package_id, day.car_type, tourPackages]);

  // Calculate transport cost based on selected package and area
  const calculateTransportCost = (packageId: string, area: string): number => {
    if (!packageId) return 0;
    const tourPackage = (tourPackages as TourPackageItem[]).find((pkg) => pkg.id === packageId);
    if (!tourPackage) return 0;
    const carType = day.car_type || 'MPV';

    // Determine which transport cost to use based on car type
    let transportCost = 0;
    if (area === "Kuta") {
      if (carType === "MPV") transportCost = Number(tourPackage.transport_kuta || 0);
      else if (carType === "ELF Short") transportCost = Number(tourPackage.elf_short_transport_kuta || 0);
      else if (carType === "Hiace") transportCost = Number(tourPackage.hiace_transport_kuta || 0);
      else if (carType === "ELF Giga") transportCost = Number(tourPackage.elf_giga_transport_kuta || 0);
      else if (carType === "Bus") transportCost = Number(tourPackage.bus_transport_kuta || 0);
    } else if (area === "Nusa Dua") {
      if (carType === "MPV") transportCost = Number(tourPackage.transport_nusadua || 0);
      else if (carType === "ELF Short") transportCost = Number(tourPackage.elf_short_transport_nusadua || 0);
      else if (carType === "Hiace") transportCost = Number(tourPackage.hiace_transport_nusadua || 0);
      else if (carType === "ELF Giga") transportCost = Number(tourPackage.elf_giga_transport_nusadua || 0);
      else if (carType === "Bus") transportCost = Number(tourPackage.bus_transport_nusadua || 0);
    } else if (area === "Ubud") {
      if (carType === "MPV") transportCost = Number(tourPackage.transport_ubud || 0);
      else if (carType === "ELF Short") transportCost = Number(tourPackage.elf_short_transport_ubud || 0);
      else if (carType === "Hiace") transportCost = Number(tourPackage.hiace_transport_ubud || 0);
      else if (carType === "ELF Giga") transportCost = Number(tourPackage.elf_giga_transport_ubud || 0);
      else if (carType === "Bus") transportCost = Number(tourPackage.bus_transport_ubud || 0);
    } else {
      if (carType === "MPV") transportCost = Number(tourPackage.transport_other || 0);
      else if (carType === "ELF Short") transportCost = Number(tourPackage.elf_short_transport_other || 0);
      else if (carType === "Hiace") transportCost = Number(tourPackage.hiace_transport_other || 0);
      else if (carType === "ELF Giga") transportCost = Number(tourPackage.elf_giga_transport_other || 0);
      else if (carType === "Bus") transportCost = Number(tourPackage.bus_transport_other || 0);
    }
    return transportCost;
  };

  // Update captured tours when package changes
  useEffect(() => {
    if (selectedPackage) {
      const pkg = tourPackages.find((p) => p.id === selectedPackage);
      if (!pkg) return;

      // Parse the selected_tour JSON if it's a string
      let tours: TourItem[] = [];
      try {
        if (typeof pkg.selected_tour === 'string') {
          tours = JSON.parse(pkg.selected_tour);
        } else if (Array.isArray(pkg.selected_tour)) {
          tours = pkg.selected_tour;
        }
      } catch (e) {
        console.error('Error parsing selected_tour:', e);
        return;
      }

      const transportCost = calculateTransportCost(selectedPackage, transportArea);
      const dayKey = `day_${day.day_number}`;
      
      // Get the appropriate parking price field based on car type
      const getParkingPriceField = (carType: string) => {
        switch (carType) {
          case 'ELF Short':
            return 'elf_short_parking_price';
          case 'Hiace':
            return 'hiace_parking_price';
          case 'ELF Giga':
            return 'elf_giga_parking_price';
          case 'Bus':
            return 'bus_parking_price';
          case 'MPV':
          default:
            return 'mpv_parking_price';
        }
      };

      const parkingPriceField = getParkingPriceField(day.car_type || 'MPV');

      // Create the captured_tours data
      const capturedTourData: DayTourData = {
        title: `Day ${day.day_number} (${day.day_date}) - ${pkg.tour_package_name}`,
        tours: tours.map(tour => tour.tour_name),
        entrance_adult: tours.reduce((acc: { [key: string]: number }, tour: TourItem) => ({ 
          ...acc, 
          [tour.tour_name]: tour.tour_adult_price 
        }), {}),
        entrance_child: tours.reduce((acc: { [key: string]: number }, tour: TourItem) => ({ 
          ...acc, 
          [tour.tour_name]: tour.tour_child_price 
        }), {}),
        parking: tours.reduce((acc: { [key: string]: number }, tour: TourItem) => ({ 
          ...acc, 
          [tour.tour_name]: tour[parkingPriceField] 
        }), {}),
        tour_package_name: pkg.tour_package_name,
        itinerary: pkg.itinerary || ''
      };

      // Calculate total parking cost
      const totalParking = Object.values(capturedTourData.parking).reduce((sum, cost) => sum + (cost || 0), 0);
      
      // Update the day with new data
      const updatedDay = {
        ...day,
        tour_package_id: selectedPackage,
        tour_package_name: pkg.tour_package_name,
        transport_area: transportArea,
        transport_cost: transportCost,
        parking_cost: totalParking,
        captured_tours: {
          ...day.captured_tours,
          [dayKey]: capturedTourData
        }
      };
      
      onUpdate(updatedDay);
    }
  }, [selectedPackage, transportArea, day.car_type]);

  // Update captured tours when note changes
  useEffect(() => {
    // Only update if the note has actually changed from its original value
    if (note !== day.note) {
      onUpdate({
        ...day,
        note,
      });
    }
  }, [note, day.note]);

  // Calculate captured tour totals
  const calculateCapturedTourAdultEntrance = () => {
    if (!day.captured_tours || !day.captured_tours[`day_${day.day_number}`]) {
      return 0;
    }
    
    const dayTours = day.captured_tours[`day_${day.day_number}`];
    return dayTours.tours.reduce((sum: number, tour: string) => {
      return sum + (Number(dayTours.entrance_adult[tour] || 0) * Number(adult || 0));
    }, 0);
  };

  const calculateCapturedTourChildEntrance = () => {
    if (!day.captured_tours || !day.captured_tours[`day_${day.day_number}`]) {
      return 0;
    }
    
    const dayTours = day.captured_tours[`day_${day.day_number}`];
    return dayTours.tours.reduce((sum: number, tour: string) => {
      return sum + (Number(dayTours.entrance_child[tour] || 0) * Number(child || 0));
    }, 0);
  };

  const calculateCapturedTourParking = () => {
    if (!day.captured_tours || !day.captured_tours[`day_${day.day_number}`]) {
      return 0;
    }
    
    const dayTours = day.captured_tours[`day_${day.day_number}`];
    return dayTours.tours.reduce((sum: number, tour: string) => {
      return sum + Number(dayTours.parking[tour] || 0);
    }, 0);
  };

  // Update transport cost when area changes
  const handleTransportAreaChange = (value: "Kuta" | "Nusa Dua" | "Ubud" | "Other") => {
    setTransportArea(value);
    if (selectedPackage) {
      const transportCost = calculateTransportCost(selectedPackage, value);
      onUpdate({
        ...day,
        transport_area: value,
        transport_cost: transportCost,
      });
    }
  };

  // Update package selection
  const handlePackageChange = (value: string) => {
    setSelectedPackage(value);
    
    // Calculate fees and transport cost
    const fees = calculateFees(value);
    const transportCost = calculateTransportCost(value, transportArea);
    
    // Create a complete captured_tours object
    const capturedTours: CapturedToursMap = {
      ...day.captured_tours || {},
      [`day_${day.day_number}`]: {
        ...fees,
        title: `Day ${day.day_number} (${day.day_date || ''}) - ${fees.tour_package_name || ''}`
      }
    };
    
    // Update the day with all required fields
    const updatedDay = {
      ...day,
      tour_package_id: value,
      transport_cost: transportCost,
      captured_tours: capturedTours
    };
    
    // Close the popover
    setOpen(false);
    onUpdate(updatedDay);
  };

  // Add debugging to check captured_tours data
  useEffect(() => {
    if (day && typeof day === 'object') {
      console.log(`Day ${day.day_number} data:`, day);
      console.log(`Day ${day.day_number} captured_tours:`, day.captured_tours);
    }
  }, [day]);

  const formatIDR = (value: number) => {
    return new Intl.NumberFormat("id-ID").format(value);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-4">
            <CardTitle>Day {day.day_number}</CardTitle>
            <Input
              type="date"
              value={day.day_date}
              onChange={(e) => onUpdate({ ...day, day_date: e.target.value })}
              className="w-40 h-8"
            />
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-3">
            <div className="w-[70%] space-y-2">
              <Label>Tour Package</Label>
              <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between"
                  >
                    {day.tour_package_id
                      ? (tourPackages as TourPackageItem[]).find((p) => p.id === day.tour_package_id)?.tour_package_name
                      : "Select tour package..."}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[100%] p-0">
                  <div className="flex flex-col">
                    <Input
                      placeholder="Search tour package..."
                      className="border-0 focus-visible:ring-0"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                    />
                    <ScrollArea className="h-[300px]">
                      {filteredPackages.length > 0 ? (
                        filteredPackages.map((pkg) => (
                          <Button
                            key={pkg.id}
                            variant="ghost"
                            role="option"
                            className="w-full justify-start gap-2"
                            onClick={() => {
                              handlePackageChange(pkg.id);
                              setOpen(false);
                              setSearch("");
                            }}
                          >
                            <Check
                              className={cn(
                                "h-4 w-4",
                                day.tour_package_id === pkg.id
                                  ? "opacity-100"
                                  : "opacity-0"
                              )}
                            />
                            {pkg.tour_package_name}
                          </Button>
                        ))
                      ) : (
                        <p className="p-4 text-sm text-muted-foreground">
                          No tour package found.
                        </p>
                      )}
                    </ScrollArea>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <div className="w-[30%] space-y-2">
              <Label>Transport Area</Label>
              <Select
                value={transportArea}
                onValueChange={handleTransportAreaChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select area" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Kuta">Kuta</SelectItem>
                  <SelectItem value="Nusa Dua">Nusa Dua</SelectItem>
                  <SelectItem value="Ubud">Ubud</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {day.tour_package_id && (
            <>
              <div className="grid grid-cols-6 gap-2">
                <div className="col-span-1 space-y-1">
                  <Label className="text-xs">Entrance Fee/Adult</Label>
                  <Input
                    type="number"
                    value={Object.values(currentFees.entrance_adult).reduce((a: number, b: number) => a + b, 0)}
                    disabled={!canEditPrices}
                    readOnly={!canEditPrices}
                    onChange={(e) => {
                      if (!canEditPrices) return;
                      // Update the entrance fee in the captured tours
                      const value = parseFloat(e.target.value) || 0;
                      const updatedDay = { ...day };
                      
                      // Get the day key
                      const dayKey = `day_${day.day_number}`;
                      
                      // Initialize captured_tours if it doesn't exist
                      if (!updatedDay.captured_tours) {
                        updatedDay.captured_tours = {};
                      }
                      
                      // Initialize the day entry if it doesn't exist
                      if (!updatedDay.captured_tours[dayKey]) {
                        updatedDay.captured_tours[dayKey] = { ...currentFees };
                      }
                      
                      // Distribute the value proportionally across all tours
                      const totalTours = Object.keys(currentFees.entrance_adult).length;
                      if (totalTours > 0) {
                        const valuePerTour = value / totalTours;
                        
                        // Create a new entrance_adult object with updated values
                        const updatedEntranceAdult = { ...updatedDay.captured_tours[dayKey].entrance_adult };
                        
                        Object.keys(currentFees.entrance_adult).forEach(tourId => {
                          updatedEntranceAdult[tourId] = valuePerTour;
                        });
                        
                        // Update the captured_tours with the new entrance_adult values
                        updatedDay.captured_tours[dayKey] = {
                          ...updatedDay.captured_tours[dayKey],
                          entrance_adult: updatedEntranceAdult
                        };
                      }
                      
                      onUpdate(updatedDay);
                    }}
                    className="h-8 text-sm"
                    min={0}
                    step={1000}
                    placeholder="Enter adult entrance fee"
                  />
                </div>
                {child > 0 && (
                  <div className="col-span-1 space-y-1">
                    <Label className="text-xs">Entrance Fee/Child</Label>
                    <Input
                      type="number"
                      value={Object.values(currentFees.entrance_child).reduce((a: number, b: number) => a + b, 0)}
                      onChange={(e) => {
                        // Update the entrance fee in the captured tours
                        const value = parseFloat(e.target.value) || 0;
                        const updatedDay = { ...day };
                        
                        // Get the day key
                        const dayKey = `day_${day.day_number}`;
                        
                        // Initialize captured_tours if it doesn't exist
                        if (!updatedDay.captured_tours) {
                          updatedDay.captured_tours = {};
                        }
                        
                        // Initialize the day entry if it doesn't exist
                        if (!updatedDay.captured_tours[dayKey]) {
                          updatedDay.captured_tours[dayKey] = { ...currentFees };
                        }
                        
                        // Distribute the value proportionally across all tours
                        const totalTours = Object.keys(currentFees.entrance_child).length;
                        if (totalTours > 0) {
                          const valuePerTour = value / totalTours;
                          
                          // Create a new entrance_child object with updated values
                          const updatedEntranceChild = { ...updatedDay.captured_tours[dayKey].entrance_child };
                          
                          Object.keys(currentFees.entrance_child).forEach(tourId => {
                            updatedEntranceChild[tourId] = valuePerTour;
                          });
                          
                          // Update the captured_tours with the new entrance_child values
                          updatedDay.captured_tours[dayKey] = {
                            ...updatedDay.captured_tours[dayKey],
                            entrance_child: updatedEntranceChild
                          };
                        }
                        
                        onUpdate(updatedDay);
                      }}
                      className="h-8 text-sm"
                      min={0}
                      step={1000}
                      placeholder="Enter child entrance fee"
                    />
                  </div>
                )}
                <div className="col-span-1 space-y-1">
                  <Label className="text-xs">Transport</Label>
                  <Input
                    type="number"
                    value={day.transport_cost}
                    onChange={(e) => {
                      if (!canEditPrices) return;
                      onUpdate({
                        ...day,
                        transport_cost: parseFloat(e.target.value) || 0,
                      });
                    }}
                    className="h-8 text-sm"
                    disabled={!canEditPrices}
                    readOnly={!canEditPrices}
                  />
                </div>
                <div className="col-span-1 space-y-1">
                  <Label className="text-xs">Parking Fee</Label>
                  <Input
                    type="number"
                    value={day.parking_cost}
                    onChange={(e) => {
                      if (!canEditPrices) return;
                      onUpdate({
                        ...day,
                        parking_cost: parseFloat(e.target.value) || 0,
                      });
                    }}
                    className="h-8 text-sm"
                    disabled={!canEditPrices}
                    readOnly={!canEditPrices}
                  />
                </div>
                <div className="col-span-1 space-y-1">
                  <Label className="text-xs">Meal Cost</Label>
                  <Input
                    type="number"
                    value={day.meal_cost}
                    onChange={(e) => {
                      if (!canEditPrices) return;
                      onUpdate({
                        ...day,
                        meal_cost: parseFloat(e.target.value) || 0,
                      });
                    }}
                    className="h-8 text-sm"
                    disabled={!canEditPrices}
                    readOnly={!canEditPrices}
                  />
                </div>
                <div className="col-span-1 space-y-1">
                  <Label className="text-xs">Other Cost</Label>
                  <Input
                    type="number"
                    value={day.other_cost}
                    onChange={(e) => {
                      if (!canEditPrices) return;
                      onUpdate({
                        ...day,
                        other_cost: parseFloat(e.target.value) || 0,
                      });
                    }}
                    className="h-8 text-sm"
                    disabled={!canEditPrices}
                    readOnly={!canEditPrices}
                  />
                </div>
              </div>

              <div className="mt-4">
                <Label className="text-sm font-medium">Sub Summary</Label>
                
                <div className="space-y-2 mt-2">
                  <Label className="text-xs">Tour Details</Label>
                  <div className="border rounded-lg border-gray-800">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-gray-800">
                          <th className="p-2 h-4 text-left">Tour</th>
                          <th className="p-2 h-4 text-right">Entrance/Adult</th>
                          {child > 0 && (
                            <th className="p-2 h-4 text-right">Entrance/Child</th>
                          )}
                          <th className="p-2 h-4 text-right">Parking</th>
                        </tr>
                      </thead>
                      <tbody>
                        {day.tour_package_id && (tourPackages as TourPackageItem[]).find(p => p.id === day.tour_package_id)?.selected_tour?.map((tour: TourItem, index: number) => {
                          // Get the appropriate parking price field based on car type
                          const getParkingPrice = (tour: TourItem, carType: string) => {
                            switch (carType) {
                              case 'ELF Short':
                                return tour.elf_short_parking_price || 0;
                              case 'Hiace':
                                return tour.hiace_parking_price || 0;
                              case 'ELF Giga':
                                return tour.elf_giga_parking_price || 0;
                              case 'Bus':
                                return tour.bus_parking_price || 0;
                              case 'MPV':
                              default:
                                return tour.mpv_parking_price || 0;
                            }
                          };
                          
                          const parkingPrice = getParkingPrice(tour, day.car_type || 'MPV');
                          
                          return (
                            <tr key={index} className="border-b border-gray-800 last:border-b-0 hover:bg-transparent">
                              <td className="p-2 h-4">{tour.tour_name}</td>
                              <td className="p-2 h-4 text-right">
                                <Input
                                  type="number"
                                  className="h-6 text-xs text-right w-24 inline-block"
                                  value={
                                    day.captured_tours?.[`day_${day.day_number}`]?.entrance_adult?.[tour.tour_name] !== undefined
                                      ? day.captured_tours[`day_${day.day_number}`].entrance_adult[tour.tour_name]
                                      : tour.tour_adult_price || 0
                                  }
                                  onChange={(e) => {
                                    if (!canEditPrices) return;
                                    
                                    const value = parseFloat(e.target.value) || 0;
                                    const updatedDay = { ...day };
                                    const dayKey = `day_${day.day_number}`;
                                    
                                    // Initialize captured_tours if it doesn't exist
                                    if (!updatedDay.captured_tours) {
                                      updatedDay.captured_tours = {};
                                    }
                                    
                                    // Initialize the day entry if it doesn't exist
                                    if (!updatedDay.captured_tours[dayKey]) {
                                      updatedDay.captured_tours[dayKey] = {
                                        title: `Day ${day.day_number}`,
                                        tours: [],
                                        entrance_adult: {},
                                        entrance_child: {},
                                        parking: {},
                                        tour_package_name: '',
                                        itinerary: ''
                                      };
                                    }
                                    
                                    // Initialize entrance_adult if it doesn't exist
                                    if (!updatedDay.captured_tours[dayKey].entrance_adult) {
                                      updatedDay.captured_tours[dayKey].entrance_adult = {};
                                    }
                                    
                                    // Update the specific tour's entrance fee
                                    updatedDay.captured_tours[dayKey].entrance_adult[tour.tour_name] = value;
                                    
                                    onUpdate(updatedDay);
                                  }}
                                  min={0}
                                  step={1000}
                                  disabled={!canEditPrices}
                                  readOnly={!canEditPrices}
                                />
                              </td>
                              {child > 0 && (
                                <td className="p-2 h-4 text-right">
                                  <Input
                                    type="number"
                                    className="h-6 text-xs text-right w-24 inline-block"
                                    value={
                                      day.captured_tours?.[`day_${day.day_number}`]?.entrance_child?.[tour.tour_name] !== undefined
                                        ? day.captured_tours[`day_${day.day_number}`].entrance_child[tour.tour_name]
                                        : tour.tour_child_price || 0
                                    }
                                    onChange={(e) => {
                                      if (!canEditPrices) return;
                                      
                                      const value = parseFloat(e.target.value) || 0;
                                      const updatedDay = { ...day };
                                      const dayKey = `day_${day.day_number}`;
                                      
                                      // Initialize captured_tours if it doesn't exist
                                      if (!updatedDay.captured_tours) {
                                        updatedDay.captured_tours = {};
                                      }
                                      
                                      // Initialize the day entry if it doesn't exist
                                      if (!updatedDay.captured_tours[dayKey]) {
                                        updatedDay.captured_tours[dayKey] = {
                                          title: `Day ${day.day_number}`,
                                          tours: [],
                                          entrance_adult: {},
                                          entrance_child: {},
                                          parking: {},
                                          tour_package_name: '',
                                          itinerary: ''
                                        };
                                      }
                                      
                                      // Initialize entrance_child if it doesn't exist
                                      if (!updatedDay.captured_tours[dayKey].entrance_child) {
                                        updatedDay.captured_tours[dayKey].entrance_child = {};
                                      }
                                      
                                      // Update the specific tour's entrance fee
                                      updatedDay.captured_tours[dayKey].entrance_child[tour.tour_name] = value;
                                      
                                      onUpdate(updatedDay);
                                    }}
                                    min={0}
                                    step={1000}
                                    disabled={!canEditPrices}
                                    readOnly={!canEditPrices}
                                  />
                                </td>
                              )}
                              <td className="p-2 h-4 text-right">
                                <Input
                                  type="number"
                                  className="h-6 text-xs text-right w-24 inline-block"
                                  value={
                                    day.captured_tours?.[`day_${day.day_number}`]?.parking?.[tour.tour_name] !== undefined
                                      ? day.captured_tours[`day_${day.day_number}`].parking[tour.tour_name]
                                      : getParkingPrice(tour, day.car_type || 'MPV')
                                  }
                                  onChange={(e) => {
                                    if (!canEditPrices) return;
                                    
                                    const value = parseFloat(e.target.value) || 0;
                                    const updatedDay = { ...day };
                                    const dayKey = `day_${day.day_number}`;
                                    
                                    // Initialize captured_tours if it doesn't exist
                                    if (!updatedDay.captured_tours) {
                                      updatedDay.captured_tours = {};
                                    }
                                    
                                    // Initialize the day entry if it doesn't exist
                                    if (!updatedDay.captured_tours[dayKey]) {
                                      updatedDay.captured_tours[dayKey] = {
                                        title: `Day ${day.day_number}`,
                                        tours: [],
                                        entrance_adult: {},
                                        entrance_child: {},
                                        parking: {},
                                        tour_package_name: '',
                                        itinerary: ''
                                      };
                                    }
                                    
                                    // Initialize parking if it doesn't exist
                                    if (!updatedDay.captured_tours[dayKey].parking) {
                                      updatedDay.captured_tours[dayKey].parking = {};
                                    }
                                    
                                    // Update the specific tour's parking fee
                                    updatedDay.captured_tours[dayKey].parking[tour.tour_name] = value;
                                    
                                    // Update the total parking cost
                                    const totalParking = Object.values(updatedDay.captured_tours[dayKey].parking).reduce(
                                      (sum: number, cost: number) => sum + cost,
                                      0
                                    );
                                    updatedDay.parking_cost = totalParking;
                                    
                                    onUpdate(updatedDay);
                                  }}
                                  min={0}
                                  step={1000}
                                  disabled={!canEditPrices}
                                  readOnly={!canEditPrices}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Sub Total Adult Entrance Fee</Label>
                    <Input
                      type="text"
                      className="h-8 text-sm font-medium"
                      value={formatIDR(calculateCapturedTourAdultEntrance())}
                      disabled={!canEditPrices}
                      readOnly={!canEditPrices}
                      onChange={(e) => {
                        if (!canEditPrices) return;
                        // Update the entrance fee in the captured tours
                        const value = parseFloat(e.target.value.replace(/[^0-9]/g, '')) || 0;
                        const updatedDay = { ...day };
                        
                        // Get the day key
                        const dayKey = `day_${day.day_number}`;
                        
                        // Initialize captured_tours if it doesn't exist
                        if (!updatedDay.captured_tours) {
                          updatedDay.captured_tours = {};
                        }
                        
                        // Initialize the day entry if it doesn't exist
                        if (!updatedDay.captured_tours[dayKey]) {
                          updatedDay.captured_tours[dayKey] = { ...currentFees };
                        }
                        
                        // Distribute the total value proportionally between adult and child
                        const adultTours = Object.keys(currentFees.entrance_adult).length;
                        const childTours = Object.keys(currentFees.entrance_child).length;
                        
                        if (adultTours > 0 || childTours > 0) {
                          // Calculate proportional distribution
                          const adultTotal = calculateCapturedTourAdultEntrance();
                          const childTotal = calculateCapturedTourChildEntrance();
                          const currentTotal = adultTotal + childTotal;
                          
                          if (currentTotal > 0) {
                            const adultRatio = adultTotal / currentTotal;
                            const childRatio = childTotal / currentTotal;
                            
                            // Apply new values
                            const newAdultTotal = value * adultRatio;
                            const newChildTotal = value * childRatio;
                            
                            // Create updated entrance fee objects
                            const updatedEntranceAdult = { ...updatedDay.captured_tours[dayKey].entrance_adult };
                            const updatedEntranceChild = { ...updatedDay.captured_tours[dayKey].entrance_child };
                            
                            // Distribute to individual tours
                            if (adultTours > 0) {
                              const valuePerAdultTour = (newAdultTotal / adult) / adultTours;
                              
                              Object.keys(currentFees.entrance_adult).forEach(tourId => {
                                updatedEntranceAdult[tourId] = valuePerAdultTour;
                              });
                            }
                            
                            if (childTours > 0) {
                              const valuePerChildTour = (newChildTotal / child) / childTours;
                              
                              Object.keys(currentFees.entrance_child).forEach(tourId => {
                                updatedEntranceChild[tourId] = valuePerChildTour;
                              });
                            }
                            
                            // Update the captured_tours with the new entrance fee values
                            updatedDay.captured_tours[dayKey] = {
                              ...updatedDay.captured_tours[dayKey],
                              entrance_adult: updatedEntranceAdult,
                              entrance_child: updatedEntranceChild
                            };
                          }
                        }
                        
                        onUpdate(updatedDay);
                      }}
                    />
                  </div>
                  {child > 0 && (
                    <div className="space-y-1">
                      <Label className="text-xs">Sub Total Child Entrance Fee</Label>
                      <Input
                        type="text"
                        className="h-8 text-sm font-medium"
                        value={formatIDR(calculateCapturedTourChildEntrance())}
                        disabled={!canEditPrices}
                        readOnly={!canEditPrices}
                        onChange={(e) => {
                          if (!canEditPrices) return;
                          // Update the entrance fee in the captured tours
                          const value = parseFloat(e.target.value.replace(/[^0-9]/g, '')) || 0;
                          const updatedDay = { ...day };
                          
                          // Get the day key
                          const dayKey = `day_${day.day_number}`;
                          
                          // Initialize captured_tours if it doesn't exist
                          if (!updatedDay.captured_tours) {
                            updatedDay.captured_tours = {};
                          }
                          
                          // Initialize the day entry if it doesn't exist
                          if (!updatedDay.captured_tours[dayKey]) {
                            updatedDay.captured_tours[dayKey] = { ...currentFees };
                          }
                          
                          // Distribute the total value proportionally between adult and child
                          const adultTours = Object.keys(currentFees.entrance_adult).length;
                          const childTours = Object.keys(currentFees.entrance_child).length;
                          
                          if (adultTours > 0 || childTours > 0) {
                            // Calculate proportional distribution
                            const adultTotal = calculateCapturedTourAdultEntrance();
                            const childTotal = calculateCapturedTourChildEntrance();
                            const currentTotal = adultTotal + childTotal;
                            
                            if (currentTotal > 0) {
                              const adultRatio = adultTotal / currentTotal;
                              const childRatio = childTotal / currentTotal;
                              
                              // Apply new values
                              const newAdultTotal = value * adultRatio;
                              const newChildTotal = value * childRatio;
                              
                              // Create updated entrance fee objects
                              const updatedEntranceAdult = { ...updatedDay.captured_tours[dayKey].entrance_adult };
                              const updatedEntranceChild = { ...updatedDay.captured_tours[dayKey].entrance_child };
                              
                              // Distribute to individual tours
                              if (adultTours > 0) {
                                const valuePerAdultTour = (newAdultTotal / adult) / adultTours;
                                
                                Object.keys(currentFees.entrance_adult).forEach(tourId => {
                                  updatedEntranceAdult[tourId] = valuePerAdultTour;
                                });
                              }
                              
                              if (childTours > 0) {
                                const valuePerChildTour = (newChildTotal / child) / childTours;
                                
                                Object.keys(currentFees.entrance_child).forEach(tourId => {
                                  updatedEntranceChild[tourId] = valuePerChildTour;
                                });
                              }
                              
                              // Update the captured_tours with the new entrance fee values
                              updatedDay.captured_tours[dayKey] = {
                                ...updatedDay.captured_tours[dayKey],
                                entrance_adult: updatedEntranceAdult,
                                entrance_child: updatedEntranceChild
                              };
                            }
                          }
                          
                          onUpdate(updatedDay);
                        }}
                      />
                    </div>
                  )}
                  <div className="space-y-1">
                    <Label className="text-xs">Sub Total Entrance Fee</Label>
                    <Input
                      type="text"
                      className="h-8 text-sm font-medium"
                      value={formatIDR(calculateCapturedTourAdultEntrance() + (child > 0 ? calculateCapturedTourChildEntrance() : 0))}
                      disabled={!canEditPrices}
                      readOnly={!canEditPrices}
                      onChange={(e) => {
                        if (!canEditPrices) return;
                        // This is a calculated field, but we'll allow editing and distribute the value
                        const value = parseFloat(e.target.value.replace(/[^0-9]/g, '')) || 0;
                        const updatedDay = { ...day };
                        
                        // Get the day key
                        const dayKey = `day_${day.day_number}`;
                        
                        // Initialize captured_tours if it doesn't exist
                        if (!updatedDay.captured_tours) {
                          updatedDay.captured_tours = {};
                        }
                        
                        // Initialize the day entry if it doesn't exist
                        if (!updatedDay.captured_tours[dayKey]) {
                          updatedDay.captured_tours[dayKey] = { ...currentFees };
                        }
                        
                        // Distribute the total value proportionally between adult and child
                        const adultTours = Object.keys(currentFees.entrance_adult).length;
                        const childTours = Object.keys(currentFees.entrance_child).length;
                        
                        if (adultTours > 0 || childTours > 0) {
                          // Calculate proportional distribution
                          const adultTotal = calculateCapturedTourAdultEntrance();
                          const childTotal = calculateCapturedTourChildEntrance();
                          const currentTotal = adultTotal + childTotal;
                          
                          if (currentTotal > 0) {
                            const adultRatio = adultTotal / currentTotal;
                            const childRatio = childTotal / currentTotal;
                            
                            // Apply new values
                            const newAdultTotal = value * adultRatio;
                            const newChildTotal = value * childRatio;
                            
                            // Create updated entrance fee objects
                            const updatedEntranceAdult = { ...updatedDay.captured_tours[dayKey].entrance_adult };
                            const updatedEntranceChild = { ...updatedDay.captured_tours[dayKey].entrance_child };
                            
                            // Distribute to individual tours
                            if (adultTours > 0) {
                              const valuePerAdultTour = (newAdultTotal / adult) / adultTours;
                              
                              Object.keys(currentFees.entrance_adult).forEach(tourId => {
                                updatedEntranceAdult[tourId] = valuePerAdultTour;
                              });
                            }
                            
                            if (childTours > 0) {
                              const valuePerChildTour = (newChildTotal / child) / childTours;
                              
                              Object.keys(currentFees.entrance_child).forEach(tourId => {
                                updatedEntranceChild[tourId] = valuePerChildTour;
                              });
                            }
                            
                            // Update the captured_tours with the new entrance fee values
                            updatedDay.captured_tours[dayKey] = {
                              ...updatedDay.captured_tours[dayKey],
                              entrance_adult: updatedEntranceAdult,
                              entrance_child: updatedEntranceChild
                            };
                          }
                        }
                        
                        onUpdate(updatedDay);
                      }}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
          {/* Note Field */}
          <div className="space-y-2">
            <Label htmlFor={`note-${day.day_number}`}>Day Notes</Label>
            <textarea
              id={`note-${day.day_number}`}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="w-full min-h-[100px] p-2 text-xs border border-gray-800 rounded-md"
              placeholder="Add notes for this day..."
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
